<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { useValidation } from "@/composables/useValidation";
import { TravelOrderStatus, TravelOrderStatusMap, type TravelOrderStatusMapItem } from "@/constants/trip";
import OmniAutocomplete from "@/components/selects/OmniAutocomplete.vue";
import AlertMessage from "@/components/messages/AlertMessage.vue";
import TravelOrderPayments from "@/views/trips/edit/components/TravelOrderPayments.vue";

import type { TravelOrderPayment } from "@/api/trips/dto/trip.dto";

const { t } = useI18n();
const { requiredValidator } = useValidation();

const status = defineModel<TravelOrderStatus>("status", { default: TravelOrderStatus.active });
const payments = defineModel<TravelOrderPayment[]>("payments", { default: () => [] });

const props = defineProps<{
  statusOptions: TravelOrderStatusMapItem[];
  isStatusDisabled: boolean;
  statusFromApi?: TravelOrderStatus;
}>();

const emits = defineEmits<{
  (e: "scrollBottom"): void;
}>();

const isCompleted = computed<boolean>(() => props.statusFromApi === TravelOrderStatus.completed);

const selectedStatusName = computed<string>(() => {
  return TravelOrderStatusMap.find((item) => item.id === status.value)?.name ?? "";
});
</script>

<template>
  <div class="d-flex flex-column row-gap-6 pa-5 rounded-lg travel-order-section">
    <div class="d-flex flex-column row-gap-4">
      <span class="travel-order-section__title">{{ t("trip.travel-order-details") }}</span>

      <omni-autocomplete
        v-model="status"
        :clearable="false"
        :disabled="props.isStatusDisabled || isCompleted"
        :items="props.statusOptions"
        :label="t('general.status')"
        :rules="[requiredValidator]"
        data-qa="edit-travel-order-status-field"
        required
      >
        <template #selection>{{ selectedStatusName }}</template>
      </omni-autocomplete>

      <alert-message
        v-if="props.isStatusDisabled || isCompleted"
        bg-color="indigo-lighten-5"
        border-color="none"
        class="line-height-16px"
        icon-color="indigo"
      >
        <span class="text-indigo">
          Travel order status can’t be changed, truck has already been loaded at the route stop
        </span>
      </alert-message>
    </div>

    <travel-order-payments v-model="payments" :travel-order-status="status" @scroll-bottom="emits('scrollBottom')" />
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.travel-order-section {
  border: 1px solid $grey-lighter-contrast;

  &__title {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: $grey-navy;
  }
}
</style>
